import React, { useState, useRef, useEffect } from 'react';
import {
  TextField,
  Autocomplete,
  Chip,
  Box,
  createFilterOptions,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

interface LabelOption {
  inputValue?: string;
  label: string;
  isNew?: boolean;
}

interface LabelSelectorProps {
  availableLabels: string[];
  selectedLabels: string[];
  onLabelsChange: (labels: string[]) => void;
  onAddLabel?: (label: string) => void;
}

const filter = createFilterOptions<LabelOption>();

const LabelSelector: React.FC<LabelSelectorProps> = ({
  availableLabels,
  selectedLabels,
  onLabelsChange,
  onAddLabel,
}) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: React.SyntheticEvent, newValue: (string | LabelOption)[]) => {
    const labels: string[] = [];
    
    newValue.forEach((value) => {
      if (typeof value === 'string') {
        labels.push(value);
      } else if (value.inputValue) {
        // User created a new label
        const newLabel = value.inputValue.trim();
        if (newLabel && !labels.includes(newLabel)) {
          labels.push(newLabel);
          if (onAddLabel) {
            onAddLabel(newLabel);
          }
        }
      } else {
        labels.push(value.label);
      }
    });

    onLabelsChange(labels);
    setInputValue('');
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && inputValue.trim()) {
      event.preventDefault();
      const newLabel = inputValue.trim();
      
      if (!selectedLabels.includes(newLabel)) {
        const updatedLabels = [...selectedLabels, newLabel];
        onLabelsChange(updatedLabels);
        
        if (!availableLabels.includes(newLabel) && onAddLabel) {
          onAddLabel(newLabel);
        }
      }
      
      setInputValue('');
    }
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      options={availableLabels}
      value={selectedLabels}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      filterOptions={(options, params) => {
        const filtered = filter(options.map(opt => ({ label: opt })), params);

        const { inputValue } = params;
        const isExisting = options.some((option) => inputValue === option);
        
        if (inputValue !== '' && !isExisting) {
          filtered.push({
            inputValue,
            label: `Add "${inputValue}"`,
            isNew: true,
          });
        }

        return filtered;
      }}
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return option;
        }
        if (option.inputValue) {
          return option.inputValue;
        }
        return option.label;
      }}
      renderOption={(props, option) => {
        if (typeof option === 'string') {
          return <li {...props}>{option}</li>;
        }
        
        if (option.isNew) {
          return (
            <li {...props}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AddIcon fontSize="small" color="primary" />
                <span>{option.label}</span>
              </Box>
            </li>
          );
        }
        
        return <li {...props}>{option.label}</li>;
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            label={typeof option === 'string' ? option : option.label}
            {...getTagProps({ index })}
            size="small"
            sx={{ borderRadius: 1 }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          placeholder={selectedLabels.length === 0 ? "Search or create labels..." : "Add more..."}
          onKeyDown={handleKeyDown}
          inputRef={inputRef}
          size="small"
        />
      )}
      sx={{
        width: '100%',
        '& .MuiOutlinedInput-root': {
          minHeight: 40,
          maxHeight: 200,
        },
        '& .MuiAutocomplete-listbox': {
          maxHeight: 200,
        },
      }}
    />
  );
};

export default LabelSelector;

